import React, { useEffect, useId, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { toISODate, fromISODate } from "../../utils/dates";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { GetAvailableDatesAction } from "../../store/action";

/**
 * How far ahead the months can be paged. The business's booking horizon is
 * usually shorter and the client does not know it; the days past it simply
 * come back unbookable, like a closed day.
 */
const MAX_MONTHS_AHEAD = 12;

const DAY_MS = 24 * 60 * 60 * 1000;

/** The first of the month holding `iso`, as "yyyy-MM-dd". */
const monthOf = (iso) => `${iso.slice(0, 7)}-01`;

/** `monthIso` (a first of the month) moved by `months`. */
const addMonths = (monthIso, months) => {
  const date = fromISODate(monthIso);
  date.setMonth(date.getMonth() + months);
  return toISODate(date);
};

/** The last day of the month starting at `monthIso`. */
const lastDayOf = (monthIso) => {
  const date = fromISODate(monthIso);
  return toISODate(new Date(date.getFullYear(), date.getMonth() + 1, 0));
};

/** Whole days from `fromIso` to `toIso`; rounded, so a DST change cannot shave one off. */
const daysBetween = (fromIso, toIso) =>
  Math.round((fromISODate(toIso) - fromISODate(fromIso)) / DAY_MS);

/**
 * One month as grid cells, Monday first: blanks up to the first's weekday,
 * then every day of the month. Monday because the businesses are Romanian, and
 * their customers read a week that way whatever language the page is in.
 */
const monthCells = (monthIso) => {
  const first = fromISODate(monthIso);
  const leading = (first.getDay() + 6) % 7;
  const count = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  return [
    ...Array.from({ length: leading }, () => null),
    ...Array.from({ length: count }, (_, i) =>
      toISODate(new Date(first.getFullYear(), first.getMonth(), i + 1)),
    ),
  ];
};

/** 1 January 2024 was a Monday; its week gives the column headings. */
const WEEK_OF_MONDAYS = Array.from({ length: 7 }, (_, i) => new Date(2024, 0, 1 + i));

/**
 * The month view behind the date strip's "Calendar" button: whole months at a
 * glance, for a visitor who already knows the day they want and should not have
 * to page the strip a week at a time to reach it.
 *
 * It asks the same question the strip asks — `GetAvailableDatesAction`, the
 * server's one definition of a bookable day — for everything on show in one
 * request, so a closed or fully booked day is marked as such rather than
 * offered and then answered with "no times". Like the strip it fails open: if
 * the lookup breaks, every day from today on can be picked, and picking one
 * still asks the server for its times.
 *
 * Two months side by side when there is room, one on a phone (or in a narrow
 * widget iframe), where two would shrink the days below a fingertip.
 */
const BookingCalendar = ({
  organizationSlug,
  resourceId,
  serviceId,
  selectedDate,
  initialMonth,
  todayValue,
  locale,
  onSelect,
  onDismiss,
}) => {
  const { t } = useTranslation();
  const idPrefix = useId();
  const monthCount = useMediaQuery("(min-width: 720px)") ? 2 : 1;

  const currentMonth = monthOf(todayValue);
  const lastMonth = addMonths(currentMonth, MAX_MONTHS_AHEAD);

  const [firstMonth, setFirstMonth] = useState(() => {
    const wanted = initialMonth ? monthOf(initialMonth) : currentMonth;
    return wanted < currentMonth ? currentMonth : wanted;
  });

  const months = Array.from({ length: monthCount }, (_, i) => addMonths(firstMonth, i));
  const shownLast = months[months.length - 1];

  /** Free days on show as a Set of "yyyy-MM-dd"; null while loading or after a failed lookup. */
  const [freeDates, setFreeDates] = useState(null);
  const [loading, setLoading] = useState(true);

  // Nothing before today can be booked, so the lookup starts there in the
  // current month. Everything on show is one request: two months fit well
  // inside the server's cap on a single scan.
  const rangeStart = firstMonth < todayValue ? todayValue : firstMonth;
  const rangeEnd = lastDayOf(shownLast);

  useEffect(() => {
    // Paging quickly must not let an older answer land last.
    let stale = false;

    const fetchFreeDates = async () => {
      setLoading(true);
      setFreeDates(null);
      try {
        const dates = await GetAvailableDatesAction(
          organizationSlug,
          resourceId,
          serviceId,
          rangeStart,
          daysBetween(rangeStart, rangeEnd) + 1,
        );
        if (!stale) setFreeDates(Array.isArray(dates) ? new Set(dates) : null);
      } catch (err) {
        console.error("Error fetching calendar dates:", err);
        if (!stale) setFreeDates(null);
      } finally {
        if (!stale) setLoading(false);
      }
    };

    fetchFreeDates();

    return () => {
      stale = true;
    };
  }, [organizationSlug, resourceId, serviceId, rangeStart, rangeEnd]);

  const canGoBack = firstMonth > currentMonth;
  const canGoForward = shownLast < lastMonth;

  /** One part of a date, without the dot Romanian abbreviations end in ("mie." → "mie"). */
  const datePart = (date, options) =>
    date.toLocaleDateString(locale, options).replace(/\.$/, "");

  const monthTitle = (monthIso) => {
    const title = fromISODate(monthIso).toLocaleDateString(locale, {
      month: "long",
      year: "numeric",
    });
    return title.charAt(0).toUpperCase() + title.slice(1);
  };

  const weekdays = WEEK_OF_MONDAYS.map((date) => datePart(date, { weekday: "short" }));

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onDismiss?.();
    }
  };

  const navButton = (direction) => {
    const back = direction === "back";
    return (
      <button
        type="button"
        className="date-nav-btn"
        onClick={() => setFirstMonth((month) => addMonths(month, back ? -1 : 1))}
        disabled={back ? !canGoBack : !canGoForward}
        aria-label={t(back ? "Booking.previousMonth" : "Booking.nextMonth")}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d={back ? "M15 5l-7 7 7 7" : "M9 5l7 7-7 7"} />
        </svg>
      </button>
    );
  };

  return (
    <div className="booking-calendar" onKeyDown={handleKeyDown}>
      {loading && (
        <span className="sr-only" role="status">
          {t("Booking.loadingDates")}
        </span>
      )}
      <div
        className="booking-calendar-months"
        style={{ "--calendar-months": monthCount }}
        aria-busy={loading}
      >
        {months.map((monthIso, index) => {
          const titleId = `${idPrefix}-month-${index}`;
          return (
            <div key={monthIso} className="booking-calendar-month">
              {/* Back on the first month, forward on the last, so two months
                  side by side read as one control rather than two. */}
              <div className="booking-calendar-header">
                {index === 0 ? navButton("back") : <span aria-hidden="true" />}
                <span id={titleId} className="booking-calendar-title">
                  {monthTitle(monthIso)}
                </span>
                {index === months.length - 1 ? (
                  navButton("forward")
                ) : (
                  <span aria-hidden="true" />
                )}
              </div>

              <div className="booking-calendar-weekdays" aria-hidden="true">
                {weekdays.map((day, i) => (
                  <span key={i}>{day}</span>
                ))}
              </div>

              <div
                className="booking-calendar-grid"
                role="group"
                aria-labelledby={titleId}
              >
                {monthCells(monthIso).map((dateValue, cell) => {
                  if (!dateValue) {
                    return <span key={`blank-${cell}`} aria-hidden="true" />;
                  }

                  const isPast = dateValue < todayValue;
                  const known = freeDates !== null;
                  // Unknown after a failed lookup counts as open; unknown
                  // while loading does not, or a click would race the answer.
                  const isFree = known ? freeDates.has(dateValue) : !loading;
                  const bookable = !isPast && isFree;
                  const isSelected = selectedDate === dateValue;
                  const isToday = dateValue === todayValue;

                  const fullDate = fromISODate(dateValue).toLocaleDateString(locale, {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                  });
                  const label = [
                    isToday ? t("Booking.today") : null,
                    fullDate,
                    !isPast && known && !isFree ? t("Booking.calendarUnavailable") : null,
                  ]
                    .filter(Boolean)
                    .join(", ");

                  const className = [
                    "booking-calendar-day",
                    isPast ? "past" : "",
                    !isPast && known && isFree ? "free" : "",
                    !isPast && known && !isFree ? "unavailable" : "",
                    isToday ? "today" : "",
                    isSelected ? "selected" : "",
                  ]
                    .filter(Boolean)
                    .join(" ");

                  return (
                    <button
                      key={dateValue}
                      type="button"
                      className={className}
                      onClick={() => onSelect(dateValue)}
                      disabled={!bookable}
                      aria-pressed={isSelected}
                      aria-label={label}
                    >
                      {fromISODate(dateValue).getDate()}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="booking-calendar-legend" aria-hidden="true">
        <span className="booking-calendar-legend-item">
          <span className="booking-calendar-swatch free" />
          {t("Booking.calendarAvailable")}
        </span>
        <span className="booking-calendar-legend-item">
          <span className="booking-calendar-swatch unavailable" />
          {t("Booking.calendarUnavailable")}
        </span>
      </div>
    </div>
  );
};

BookingCalendar.propTypes = {
  organizationSlug: PropTypes.string.isRequired,
  resourceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  serviceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  /** "yyyy-MM-dd", or empty when nothing is picked yet. */
  selectedDate: PropTypes.string,
  /** Any day of the month to open on ("yyyy-MM-dd"); a past one opens on this month. */
  initialMonth: PropTypes.string,
  /** Today on the visitor's own calendar, "yyyy-MM-dd". */
  todayValue: PropTypes.string.isRequired,
  /** The page's language, which the month and day names follow. */
  locale: PropTypes.string,
  /** Called with the picked day, "yyyy-MM-dd". */
  onSelect: PropTypes.func.isRequired,
  /** Escape: close without picking. */
  onDismiss: PropTypes.func,
};

export default BookingCalendar;
